<template>
	<BaseModal
		:hasHeader="false"
		@mouseover.native="onMouseOver"
		@mouseleave.native="onMouseLeave"
		@dismiss="$emit('dismiss')"
		position="bottom"
	>
		<div class="poll-modal">
			<div class="poll-modal__header">
				<FontAwesomeIcon class="poll-modal__check-icon" :icon="faCheckCircle" />
				<div class="poll-modal__title">{{ modalTitle }}</div>
				<div class="poll-modal__close" @click="$emit('dismiss')">
					<FontAwesomeIcon :icon="faTimes" />
				</div>
			</div>
			<div class="poll-modal__separator"></div>
			<div class="poll-modal__body">
				<div class="poll-modal__question-text">{{ questionText }}</div>
				<div class="poll-modal__btns">
					<ChipButton active class="poll-modal__btn poll-modal__like-btn">
						<a @click="onLike"> <FontAwesomeIcon :icon="faThumbsUp" /> {{ $t('WEBAPP_LIKE') }}</a>
					</ChipButton>
					<ChipButton active class="poll-modal__btn poll-modal__dislike-btn">
						<a @click="onDislike"> <FontAwesomeIcon :icon="faThumbsDown" /> {{ $t('WEBAPP_DISLIKE') }}</a>
					</ChipButton>
				</div>
			</div>
		</div>
	</BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

import { ObjectType } from '@/@types/graphql-types';
import type { ListMutationTitleDetailParam } from '@/helpers/composables/useListActions';

import BaseModal from '@/components/modals/vfm/BaseModal.vue';
import ChipButton from '@/ui-components/ChipButton.vue';

import { faCheckCircle, faTimes, faThumbsUp, faThumbsDown } from '@fortawesome/free-solid-svg-icons';
import { useListActions } from '@/helpers/composables/useListActions';

const props = defineProps<{
	modalTitle: string;
	titleId: string;
	titleDetails: ListMutationTitleDetailParam;
}>();
const emit = defineEmits(['dismiss']);

const { t } = useI18n();
const { setInDislikelist, setInLikelist } = useListActions();

const timeoutID = ref<ReturnType<typeof setTimeout> | null>(null);

const questionText = computed(() => {
	const titleType = props.titleDetails.objectType === ObjectType.Movie ? 'WEBAPP_MOVIE' : 'WEBAPP_SHOW';
	return t('WEBAPP_DID_YOU_LIKE_THE', { titleType: t(titleType) });
});

function onLike() {
	setInLikelist(props.titleId, false, false, props.titleDetails, {
		source: 'like_dislike_poll_modal',
	});
	emit('dismiss');
}

function onDislike() {
	setInDislikelist(props.titleId, false, false, props.titleDetails, {
		source: 'like_dislike_poll_modal',
	});
	emit('dismiss');
}

function onMouseOver() {
	if (timeoutID.value) {
		clearTimeout(timeoutID.value);
	}
}

function onMouseLeave() {
	setTimeoutToClose();
}

function setTimeoutToClose() {
	timeoutID.value = setTimeout(() => emit('dismiss'), 4500);
}

onMounted(() => {
	setTimeoutToClose();
});
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/typography.scss';

.jw-poll-modal {
	flex-direction: column;

	& > .hydrated {
		display: none;
	}

	.base-modal {
		border-radius: 6px;
		pointer-events: all;
	}

	section {
		display: flex;
	}

	.modal-wrapper {
		width: 700px;
		height: 101px;
		background: transparent;
		margin: auto 8px 20px;

		@media (max-width: $screen-sm) {
			width: calc(100% - 16px);
		}
	}
	.poll-modal {
		margin-top: auto;
		padding: 12px 14px;
		width: 100%;
		height: 101px;
		background: rgb(28, 37, 47);
		border-radius: 6px;

		&__title {
			@include jw-text-small();

			color: rgb(255, 255, 255);
			font-weight: normal;
			padding-inline-end: 12px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&__header {
			display: flex;
			align-items: center;
		}

		&__close {
			display: flex;
			align-items: center;
			font-size: 20px;
			margin-inline-start: auto;
			cursor: pointer;
		}

		&__check-icon {
			color: #fbc500;
			font-size: 18px;
			margin-inline-end: 8px;
		}

		&__separator {
			width: 356px;
			height: 1px;
			background: #222930;
			border-radius: 0px;
			margin: 12px 0 9px;
		}

		&__body {
			display: flex;
			align-items: center;
		}

		&__btns {
			display: flex;
			margin-inline-start: auto;
			align-items: center;
		}

		&__btn {
			margin-bottom: 0;

			a {
				cursor: pointer;
			}

			svg {
				margin-inline-end: 8px;
			}
		}

		&__like-btn {
			margin-inline-end: 12px;
		}
	}
}
</style>
